import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import businessFlowsTheme, { brand } from "../../theme/businessFlowsTheme";

const newReference = () => Math.random().toString(36).slice(2, 8).toUpperCase();

// A page that breaks while drawing shows this instead of a blank screen: what
// happened, how to get going again, and a reference for support. The rest of
// the app - header, navigation - keeps working. Keyed on the route by its
// parent, so moving to another page clears it.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reference: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error, reference: newReference() };
  }

  componentDidCatch(error, info) {
    // Logged with the reference the user sees, so a report can be matched.
    console.error(`[${this.state.reference || "ui"}] ${window.location.pathname}:`, error, info?.componentStack);
  }

  details() {
    const { error, reference } = this.state;
    return [
      `Reference: ${reference}`,
      `Page: ${window.location.pathname}`,
      `Time: ${new Date().toISOString()}`,
      `Error: ${error?.message || String(error)}`,
    ].join("\n");
  }

  copy = async () => {
    try {
      await navigator.clipboard.writeText(this.details());
      this.setState({ copied: true });
    } catch (err) {
      this.setState({ copied: false });
    }
  };

  render() {
    const { error, reference, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <ThemeProvider theme={businessFlowsTheme}>
        <Box className="bfx" sx={{ height: "100%", display: "grid", placeItems: "center", p: 3, bgcolor: "background.default" }}>
          <Stack
            spacing={2}
            alignItems="center"
            sx={{ maxWidth: 460, textAlign: "center", p: 4, borderRadius: 4, bgcolor: "#fff", border: `1px solid ${brand.line}` }}
          >
            <Box sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
              <ReportGmailerrorredOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                This page ran into a problem
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Nothing you saved is affected. Try the page again, or go back to the start. If it keeps happening, send us the
                reference below.
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 0.75, borderRadius: 2, bgcolor: brand.canvas, border: `1px solid ${brand.line}`, fontWeight: 700, letterSpacing: "0.08em" }}>
              {reference}
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={() => window.location.reload()}>
                Try again
              </Button>
              <Button variant="outlined" startIcon={<HomeOutlinedIcon />} onClick={() => window.location.assign("/home")}>
                Go to start
              </Button>
              <Button startIcon={<ContentCopyOutlinedIcon />} onClick={this.copy}>
                {copied ? "Copied" : "Copy details"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }
}
